import { memo, useRef, useState, useCallback, useMemo } from 'react';

import Svg from 'lib/svgs.json';
import Link from 'next/link';
import styled from 'styled-components';
import { mqLargeMin, mqSXLargeMin } from 'styled/tools/media-queries';
import { WithSiteLayoutPadding } from 'styles/commonCss';
import { putOverlay } from 'lib/utils/put-overlay';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { createMarkup } from 'lib/utils/create-markup';
import { handleize } from 'lib/utils/handleize';

const SiteFilterStyled = styled.div`
  width: 100%;

  * {
    font-size: 16px;
  }

  &.active {
    .head {
      .title {
        font-weight: 500;
      }
      .icon svg {
        transform: translateY(-2px) rotate(180deg);
      }
    }
  }

  &.site-filter-lg {
    padding: 0 var(--spacing-48) 0 0;
    position: relative;
    width: 100%;
    border: 0;
    margin: 0;
  }

  &.site-filter-sm {
    transition: all 0.25s ease;
    transform: translateY(-80px);
    ${WithSiteLayoutPadding}
    border-bottom: 1px solid var(--outline-neutral);
    background-color: #fff;
    position: fixed;
    z-index: 25;
    margin: 0;
    left: 0;
    top: 0;

    .head {
      height: 60px;

      .text {
        width: 100%;
      }
    }

    .body {
      .list-item {
        line-height: 16px;
        margin-bottom: 0;
        padding: 10px 0;
        .tag {
          text-align: left;
          width: 100%;
          border: none;
          font-size: 14px;
        }
      }
      .list-item.active {
        font-weight: 500;
      }
    }
  }

  html.with-scrolled &.site-filter-sm {
    transform: translateY(0);
    @media ${mqLargeMin} {
      display: none;
    }
  }

  .filter-mobile & {
    &.active {
      .head {
        .icon svg {
          transform: translateY(-2px) rotate(180deg);
        }
      }
    }

    &.site-filter-sm {
      margin: 50px 0 50px;
      transform: none;
      position: relative;
      border-top: 1px solid #1c3a1333;
      border-bottom: 1px solid #1c3a1333;
      padding: 0;
      z-index: 1;

      .head {
        border: 0;
        padding: 0;
        .text {
          width: 100%;
        }
        .title {
          display: none;
        }
        .icon {
          margin-left: auto;
          svg {
            transform: translateY(2px);
          }
        }
        .tag {
          font-size: 14px;
        }
      }

      .body {
        padding: 0;
        .list {
          padding: 0 0 25px 0;
        }

        .list-item {
          padding: 10px 0;
          &:nth-child(2) {
            padding: 0 0 10px;
          }
        }
      }
    }
  }
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  height: 50px;

  @media ${mqLargeMin} {
    display: none;
  }
`;

const Text = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
`;

const Title = styled.div`
  font-weight: 500;
  text-transform: capitalize;
  margin-right: var(--spacing-8);
`;

const Tag = styled.div`
  text-overflow: ellipsis;
  overflow: ellipsis;
  white-space: nowrap;
  width: 200px;
`;

const HeadIcon = styled.div`
  height: 19px;
  width: 20px;
  overflow: hidden;
  margin-left: auto;
  transform: scale(1.25) rotate(180deg) translateX(7px);

  svg {
    transition: all 0.25s ease;
  }
`;

const Body = styled.div`
  transition: all 0.5s ease;
  overflow: hidden;
  width: 100%;

  @media ${mqLargeMin} {
    transition: none;
    max-height: none !important;
  }
`;

const List = styled.div`
  display: block;
  padding-bottom: 25px;
  @media ${mqLargeMin} {
    padding-bottom: 0;
  }
`;

const ListItem = styled.div`
  line-height: 16px;
  margin-bottom: 0;
  padding: 5px 0;

  .tag {
    text-align: left;
    width: 100%;
    border: none;
    font-size: 14px;
  }

  &.active {
    font-weight: 500;
  }

  transition: opacity 0.5s ease;
  margin-bottom: var(--spacing-24);
  cursor: pointer;
  opacity: 0.8;

  @media ${mqLargeMin} {
    margin-bottom: var(--spacing-8);
  }

  &:hover {
    opacity: 1;
  }
  &:last-child {
    margin-bottom: 0;
  }
  &.active {
    display: none;
    opacity: 1;
    @media ${mqLargeMin} {
      display: block;
    }
  }
  &.active:before {
    display: none;
    font-size: 13px;
    margin-right: var(--spacing-8);
    @media ${mqLargeMin} {
      display: inline-block;
    }
  }

  &.sub-item {
    margin-left: 35px;
  }
`;

const LinkStyled = styled(Link)`
  text-decoration: none;
`;

type Props = {
  handleFilter: (filter: string) => void;
  list: any[];
  title: string;
  stickyMargin?: boolean;
  overlay?: boolean;
  isSub?: (a: string) => boolean;
  activeFilter: string;
};

const SiteFilter = memo((props: Props) => {
  const {
    handleFilter,
    list,
    title,
    stickyMargin,
    overlay,
    isSub,
    activeFilter,
  } = props;

  const hasLen = list.length > 0;
  const stickyFor = stickyMargin ? 1024 : null;
  const listEl = useRef<HTMLDivElement>(undefined);
  const containerRef = useRef<HTMLDivElement>(undefined);
  const size = useClientSideMediaQuery(mqSXLargeMin) ? 'lg' : 'sm';
  const [active, setActive] = useState(false);

  const handleFilterClick = (e, v) => {
    e.preventDefault();
    setActive(false);
    handleFilter(v);
    if (size === 'sm' && overlay) putOverlay();
  };

  const handleHeadClick = useCallback(() => {
    setActive(!active);

    if (size === 'sm' && overlay) {
      putOverlay();
    }
  }, [active, listEl]);

  const styles = useMemo(
    () => ({
      maxHeight: active ? `${listEl?.current?.offsetHeight}px` : 0,
    }),
    [active],
  );

  const classes = useMemo(() => {
    return `site-filter site-filter-${size} ${active ? 'active' : ''}`;
  }, [size, active]);

  return (
    (hasLen && (
      <SiteFilterStyled
        className={classes}
        data-no-print
        data-sticky-for={stickyFor}
        data-margin-top={stickyMargin}
      >
        {size === 'sm' && (
          <Head className='head' ref={containerRef} onClick={handleHeadClick}>
            <Text className='text'>
              <Title className='title'>{title}</Title>
              <Tag className='tag'>{activeFilter}</Tag>
              <HeadIcon
                className='icon'
                dangerouslySetInnerHTML={createMarkup(Svg.angle)}
              />
            </Text>
          </Head>
        )}

        <Body className='body' style={styles}>
          <List ref={listEl} className='list'>
            {list.map((v, i) => {
              const active = activeFilter === v ? 'active' : '';
              const isSubItem = isSub && isSub(v) ? 'sub-item' : '';
              return (
                <LinkStyled href={`#${handleize(v)}`} key={i} tabIndex={0}>
                  <ListItem
                    className={['list-item', active, isSubItem].join(' ')}
                    onClick={(e) => handleFilterClick(e, v)}
                  >
                    <span className='tag'>{v}</span>
                  </ListItem>
                </LinkStyled>
              );
            })}
          </List>
        </Body>
      </SiteFilterStyled>
    )) || <div />
  );
});

export default SiteFilter;
